// Pure layout math for the calendar grid. No DOM access — unit-tested in
// layout.test.js and bundled into the app via grid.js.

// Assign overlapping timed events to columns (Google-Calendar style).
// Input:  [{ id, startSlot, dur }]
// Output: { [id]: { col, nCols } } — col is 0-based, nCols = columns in cluster.
export function assignOverlapColumns(events) {
  const result = {};
  if (!events.length) return result;

  const sorted = [...events].sort(
    (a, b) => a.startSlot - b.startSlot || b.dur - a.dur
  );

  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (!cluster.length) return;
    const colEnds = []; // last occupied end-slot per column
    const colOf = new Map(); // id → assigned column (no mutation of caller objects)
    for (const ev of cluster) {
      const end = ev.startSlot + ev.dur;
      let placed = colEnds.findIndex(e => e <= ev.startSlot);
      if (placed === -1) { placed = colEnds.length; colEnds.push(end); }
      else colEnds[placed] = end;
      colOf.set(ev.id, placed);
    }
    const nCols = colEnds.length;
    for (const ev of cluster) result[ev.id] = { col: colOf.get(ev.id), nCols };
    cluster = [];
  };

  for (const ev of sorted) {
    const end = ev.startSlot + ev.dur;
    if (cluster.length && ev.startSlot >= clusterEnd) {
      flush();
      clusterEnd = -Infinity;
    }
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, end);
  }
  flush();

  return result;
}

// Pack multi-day all-day events into stacked rows so they never overlap.
// Input:  [{ id, startDay, endDay }] (day indices 0–6, inclusive)
// Output: { [id]: rowIndex }
export function assignAllDayRows(events) {
  const result = {};
  const sorted = [...events].sort(
    (a, b) => a.startDay - b.startDay || a.endDay - b.endDay
  );
  const rowEnds = []; // last occupied endDay per row
  for (const ev of sorted) {
    let row = rowEnds.findIndex(end => end < ev.startDay);
    if (row === -1) { row = rowEnds.length; rowEnds.push(ev.endDay); }
    else rowEnds[row] = ev.endDay;
    result[ev.id] = row;
  }
  return result;
}

// Find which blocks and timed events overlap each other within one day, so
// the grid can move those events to a side rail instead of drawing them on
// top of the block titles.
// Input:  blocks [{ id, startSlot, dur }], events [{ id, startSlot, dur }]
// Output: { blockIds: Set, eventIds: Set } — ids that overlap the other kind.
export function findBlockEventOverlaps(blocks, events) {
  const blockIds = new Set();
  const eventIds = new Set();
  for (const b of blocks) {
    const bEnd = b.startSlot + b.dur;
    for (const ev of events) {
      if (ev.startSlot < bEnd && b.startSlot < ev.startSlot + ev.dur) {
        blockIds.add(b.id);
        eventIds.add(ev.id);
      }
    }
  }
  return { blockIds, eventIds };
}
